import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowUpRight, X } from "@untitledui/icons";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useActivePopups, useDismissProductUpdate } from "@/api/product-updates";
import { formatShortDate } from "@/utils/date-conversion";

const ANIMATION_DURATION_MS = 300;
const MAX_VISIBLE = 4;
const MAX_CONTENT_HEIGHT = 520;
const SCROLL_BOTTOM_THRESHOLD = 8;

type PopupUpdate = {
  id: string;
  title: string;
  copy: string;
  thumbnailUrl: string | null;
  publishedAt: string | null;
};

const plainCopy = (copy: string) => copy.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/^- /gm, "");

const ExpandableCopy = ({ copy, onExpand }: { copy: string; onExpand: () => void }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight + 1);
  }, [copy]);

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(true);
    onExpand();
  };

  return (
    <div className="relative">
      <p ref={textRef} className={`text-sm text-tertiary leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
        {plainCopy(copy)}
      </p>
      {!expanded && isClamped && (
        <button
          type="button"
          aria-label="Show more"
          onClick={handleExpand}
          className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-bg-primary to-transparent"
        />
      )}
    </div>
  );
};

const FeaturedUpdate = ({
  update,
  onClick,
  onExpand,
}: {
  update: PopupUpdate;
  onClick: () => void;
  onExpand: () => void;
}) => (
  // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation handled at popup level
  // biome-ignore lint/a11y/noStaticElementInteractions: keyboard navigation handled at popup level
  <div
    onClick={onClick}
    className="w-full text-left flex flex-col gap-2 px-5 pt-5 pb-3 hover:bg-secondary/40 transition-colors cursor-pointer"
  >
    {update.publishedAt && (
      <span className="text-xs font-medium text-quaternary">{formatShortDate(update.publishedAt)}</span>
    )}
    <h3 className="text-lg font-semibold text-primary leading-snug">{update.title}</h3>
    <ExpandableCopy copy={update.copy} onExpand={onExpand} />
    {update.thumbnailUrl && <img src={update.thumbnailUrl} alt="" className="w-full rounded-lg object-cover mt-1" />}
  </div>
);

const MiniTimelineEntry = ({
  update,
  isLast,
  onClick,
  onExpand,
}: {
  update: PopupUpdate;
  isLast: boolean;
  onClick: () => void;
  onExpand: () => void;
}) => (
  // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation handled at popup level
  // biome-ignore lint/a11y/noStaticElementInteractions: keyboard navigation handled at popup level
  <div
    onClick={onClick}
    className="relative w-full text-left flex gap-3 px-5 py-2 hover:bg-secondary/40 transition-colors cursor-pointer"
  >
    <div className="flex flex-col items-center pt-1.5 shrink-0">
      <div className="size-2 rounded-full bg-gray-300 shrink-0" />
      {!isLast && <div className="flex-1 w-px border-l border-dashed border-gray-300 mt-1" />}
    </div>
    <div className={`flex flex-col gap-0.5 min-w-0 w-full ${isLast ? "" : "pb-2"}`}>
      {update.publishedAt && (
        <span className="text-xs font-medium text-quaternary">{formatShortDate(update.publishedAt)}</span>
      )}
      <h4 className="text-md font-semibold text-primary leading-snug">{update.title}</h4>
      <ExpandableCopy copy={update.copy} onExpand={onExpand} />
    </div>
  </div>
);

export const ProductUpdatePopup = () => {
  const { data } = useActivePopups();
  const { mutate: dismiss } = useDismissProductUpdate();
  const navigate = useNavigate();
  const routeParams = useParams({ strict: false }) as { merchantId?: string };

  const [dismissed, setDismissed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const popups: PopupUpdate[] = data?.productUpdates ?? [];

  useEffect(() => {
    setDismissed(false);
    setIsExiting(false);
  }, [popups.length]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollHeight > el.clientHeight + 1;
    setIsOverflowing(overflow);
    setAtBottom(!overflow || el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const dismissAll = useCallback(() => {
    for (const p of popups) dismiss(p.id);
  }, [popups, dismiss]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      dismissAll();
      setDismissed(true);
      setIsExiting(false);
    }, ANIMATION_DURATION_MS);
  };

  const goToUpdates = (hash?: string) => {
    if (!routeParams.merchantId) return;
    dismissAll();
    setDismissed(true);
    navigate({
      to: "/merchant/$merchantId/product-updates",
      params: { merchantId: routeParams.merchantId },
      hash,
    });
  };

  if (dismissed || popups.length === 0) return null;

  const visible = popups.slice(0, MAX_VISIBLE);
  const [featured, ...rest] = visible;

  const showGradient = isOverflowing && !atBottom;

  return (
    <div
      className={`fixed bottom-6 left-6 z-[60] w-[380px] flex flex-col rounded-xl border border-secondary bg-primary shadow-lg transition-all overflow-hidden ${
        isExiting
          ? "duration-300 ease-in opacity-0 translate-y-4"
          : "duration-300 ease-out opacity-100 translate-y-0 animate-in slide-in-from-bottom-4 fade-in"
      }`}
    >
      <button
        type="button"
        onClick={handleClose}
        className="absolute top-4 right-4 z-20 rounded-md p-1 text-fg-quaternary hover:text-fg-secondary hover:bg-quaternary transition-colors"
        aria-label="Dismiss"
      >
        <X className="size-4" />
      </button>

      <div className="relative min-h-0">
        <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: MAX_CONTENT_HEIGHT }}>
          <FeaturedUpdate update={featured} onClick={() => goToUpdates(featured.id)} onExpand={updateScrollState} />

          {rest.length > 0 && (
            <div className="border-t border-secondary py-1">
              {rest.map((update, i) => (
                <MiniTimelineEntry
                  key={update.id}
                  update={update}
                  isLast={i === rest.length - 1}
                  onClick={() => goToUpdates(update.id)}
                  onExpand={updateScrollState}
                />
              ))}
            </div>
          )}

          <div className="border-t border-secondary px-5 py-3 flex justify-center">
            <button
              type="button"
              onClick={() => goToUpdates()}
              className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-secondary transition-colors"
            >
              See all Updates
              <ArrowUpRight className="size-4" />
            </button>
          </div>
        </div>

        {showGradient && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-bg-primary to-transparent" />
        )}
      </div>
    </div>
  );
};
